/**
 * TopNav
 *
 * The customer-portal top navigation, built from WIREFRAME-DASHBOARD-v0.17 on role
 * tokens. Replaces the retired Layout sidebar.
 *
 * Legs (ENGINE-BRIEF-TOPNAV-SIDEBAR-RETIRE-v1.1):
 *  A  v0.17 visual on role tokens, no heather primitive, no raw colour, no hex.
 *  B  contracts wired: NavLink routes, the plan gate, the user menu, the theme
 *     control, the bug report, the present-org display, the verified strip.
 *  C  menus on Headless UI (already in the bundle): Menu for the More and user
 *     dropdowns. Below lg the bar yields to MobileNav (the bottom-bar model); the
 *     phase-1 hamburger drawer is retired (ENGINE-BRIEF-MOBILE-NAV-v1.0).
 *
 * Active state uses NavLink, so aria-current="page" is native. A route inside More
 * lights the More trigger.
 *
 * Theme keeps the live useTheme trio (light, dark, system), not the wireframe's
 * two-way, so System is not dropped. Org is a present-org display in phase 1
 * (Ruling 1); a switch list wraps it unchanged at phase 2. The verified strip is
 * off in phase 1 (Ruling 2); the hook returns unavailable and nothing mounts.
 */

import { NavLink, useLocation } from 'react-router-dom';
import { Menu, MenuButton, MenuItems, MenuItem } from '@headlessui/react';
import { useAuth } from '@/hooks/useAuth';
import { useTheme, ThemeIcons } from '@/hooks/useTheme';
import { cn } from '@/lib/utils';
import VerifiedStrip, { useLedgerVerification } from './TopNavVerifiedStrip';
import MobileNav from './MobileNav';
import {
  PRIMARY_NAV,
  MORE_GROUPS,
  isMoreActive,
  orgInitials,
  Icons,
  ChevronDown,
  CheckIcon,
} from './TopNavModel';

const focusRing =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-1 focus-visible:ring-offset-surface';

const menuPanel =
  'z-50 min-w-[230px] rounded-xl border border-edge bg-surface p-1.5 shadow-xl focus:outline-none';
const menuRow =
  'flex items-center gap-[11px] w-full px-2.5 py-2 rounded-md text-[13px] text-content-secondary text-left transition-colors';
const menuIc = 'w-[17px] h-[17px] shrink-0 text-content-tertiary';
const groupLabel =
  'mx-2.5 mt-1 mb-[3px] font-mono text-[9px] uppercase tracking-[0.11em] text-content-tertiary';

function primaryClass(isActive: boolean) {
  return cn(
    'px-2.5 py-1.5 rounded-md text-[13px] inline-flex items-center gap-1.5 whitespace-nowrap transition-colors',
    focusRing,
    isActive
      ? 'bg-accent-tint text-accent-strong font-semibold'
      : 'text-content-secondary font-medium hover:bg-surface-secondary hover:text-content'
  );
}

// ---------------------------------------------------------------------------
// Brand
// ---------------------------------------------------------------------------

function BrandMark() {
  return (
    <span className="w-[25px] h-[25px] rounded-[7px] bg-accent-cta text-on-accent flex items-center justify-center font-mono font-medium shrink-0">
      ~
    </span>
  );
}

function BrandWord() {
  return (
    <b className="text-[15px] font-bold tracking-[-0.01em] text-content">
      Spey<span className="text-accent font-semibold">Books</span>
    </b>
  );
}

// ---------------------------------------------------------------------------
// More menu
// ---------------------------------------------------------------------------

function MoreMenu({ pathname }: { pathname: string }) {
  const active = isMoreActive(pathname);
  return (
    <Menu as="div" className="relative inline-flex">
      <MenuButton
        className={cn(
          'px-2.5 py-1.5 rounded-md text-[13px] inline-flex items-center gap-1.5 transition-colors',
          focusRing,
          active
            ? 'bg-accent-tint text-accent-strong font-semibold'
            : 'text-content-secondary font-medium hover:bg-surface-secondary hover:text-content'
        )}
      >
        More
        <ChevronDown />
      </MenuButton>
      <MenuItems anchor={{ to: 'bottom start', gap: 8 }} className={menuPanel}>
        {MORE_GROUPS.map((group, gi) => (
          <div
            key={group.id}
            role="group"
            aria-labelledby={`more-${group.id}`}
            className={cn(gi > 0 && 'mt-[5px] pt-[5px] border-t border-edge')}
          >
            <p id={`more-${group.id}`} className={groupLabel}>
              {group.label}
            </p>
            {group.items.map((item) => {
              const Icon = Icons[item.icon];
              if (item.coming) {
                return (
                  <MenuItem key={item.label} disabled>
                    <div
                      aria-label={`${item.label}, coming`}
                      className={cn(menuRow, 'text-content-tertiary cursor-default')}
                    >
                      <span className={menuIc}><Icon /></span>
                      <span aria-hidden="true">{item.label}</span>
                      <span
                        aria-hidden="true"
                        className="ml-auto font-mono text-[8.5px] uppercase tracking-[0.06em] border border-dashed border-edge-strong rounded px-1 py-px"
                      >
                        coming
                      </span>
                    </div>
                  </MenuItem>
                );
              }
              return (
                <MenuItem key={item.label}>
                  {({ focus }) => {
                    const hot = focus;
                    return (
                      <NavLink
                        to={item.to as string}
                        className={({ isActive }) =>
                          cn(
                            menuRow,
                            hot && 'bg-surface-secondary text-content',
                            isActive && 'text-accent font-semibold'
                          )
                        }
                      >
                        <span className={menuIc}><Icon /></span>
                        <span>{item.label}</span>
                        {item.hint && (
                          <span className="ml-auto font-mono text-[10px] text-content-tertiary tracking-[0.02em]">
                            {item.hint}
                          </span>
                        )}
                      </NavLink>
                    );
                  }}
                </MenuItem>
              );
            })}
          </div>
        ))}
      </MenuItems>
    </Menu>
  );
}

// ---------------------------------------------------------------------------
// Present-org display (phase 1). A switch list wraps this unchanged at phase 2.
// ---------------------------------------------------------------------------

function OrgDisplay({ name }: { name: string }) {
  if (!name) return null;
  return (
    <div
      className="flex items-center gap-2 rounded-[9px] px-2 py-1 max-w-[200px] shrink-0"
      aria-label={`Organisation: ${name}`}
    >
      <span className="w-6 h-6 rounded-md bg-accent-tint text-accent-strong flex items-center justify-center font-mono text-[10px] font-semibold shrink-0">
        {orgInitials(name)}
      </span>
      <span className="text-[13px] font-semibold text-content truncate max-w-[100px] sm:max-w-[150px] lg:max-w-[200px]">
        {name}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// User menu
// ---------------------------------------------------------------------------

function UserMenu({ onOpenBugReport }: { onOpenBugReport: () => void }) {
  const { user, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const initial = user?.fullName?.charAt(0)?.toUpperCase() || 'U';

  const themeOptions = [
    { value: 'light' as const, label: 'Light', Icon: ThemeIcons.Sun },
    { value: 'dark' as const, label: 'Dark', Icon: ThemeIcons.Moon },
    { value: 'system' as const, label: 'System', Icon: ThemeIcons.System },
  ];

  return (
    <Menu as="div" className="relative inline-flex shrink-0">
      <MenuButton
        aria-label="Account menu"
        className={cn(
          'flex items-center gap-[7px] rounded-[9px] border border-transparent px-1 py-[3px] hover:bg-surface-secondary transition-colors',
          focusRing
        )}
      >
        <span className="w-[30px] h-[30px] rounded-full bg-accent-tint text-accent-strong flex items-center justify-center font-semibold text-[13px]">
          {initial}
        </span>
        <ChevronDown />
      </MenuButton>
      <MenuItems anchor={{ to: 'bottom end', gap: 8 }} className={cn(menuPanel, 'min-w-[240px]')}>
        <div className="px-2.5 pt-2 pb-[9px] border-b border-edge mb-1.5">
          <div className="font-semibold text-[13px] text-content truncate">{user?.fullName}</div>
          <div className="font-mono text-[11px] text-content-tertiary mt-0.5 truncate">{user?.email}</div>
        </div>

        <MenuItem>
          {({ focus }) => {
            const hot = focus;
            return (
              <NavLink
                to="/settings"
                className={({ isActive }) =>
                  cn(menuRow, hot && 'bg-surface-secondary text-content', isActive && 'text-accent font-semibold')
                }
              >
                <span className={menuIc}><Icons.settings /></span>
                <span>Settings</span>
              </NavLink>
            );
          }}
        </MenuItem>

        <MenuItem>
          {({ focus }) => {
            const hot = focus;
            return (
              <button type="button" onClick={onOpenBugReport} className={cn(menuRow, hot && 'bg-surface-secondary text-content')}>
                <span className={menuIc}><Icons.bug /></span>
                <span>Report a bug</span>
              </button>
            );
          }}
        </MenuItem>

        <div role="group" aria-labelledby="usr-theme" className="mt-[5px] pt-[5px] border-t border-edge">
          <p id="usr-theme" className={groupLabel}>Theme</p>
          {themeOptions.map((opt) => {
            const current = theme === opt.value;
            return (
              <MenuItem key={opt.value}>
                {({ focus }) => {
                  const hot = focus;
                  return (
                    <button
                      type="button"
                      onClick={() => setTheme(opt.value)}
                      className={cn(menuRow, hot && 'bg-surface-secondary text-content', current && 'text-content')}
                    >
                      <span className={menuIc}><opt.Icon /></span>
                      <span>{opt.label}</span>
                      {current && <span className="sr-only"> (current)</span>}
                      {current && (
                        <span className="ml-auto text-accent">
                          <CheckIcon />
                        </span>
                      )}
                    </button>
                  );
                }}
              </MenuItem>
            );
          })}
        </div>

        {user?.isAdmin && (
          <MenuItem>
            {({ focus }) => {
              const hot = focus;
              return (
                <NavLink
                  to="/admin"
                  className={({ isActive }) =>
                    cn(menuRow, 'text-attention', hot && 'bg-surface-secondary', isActive && 'font-semibold')
                  }
                >
                  <span className={cn(menuIc, 'text-attention')}><Icons.admin /></span>
                  <span>Admin panel</span>
                </NavLink>
              );
            }}
          </MenuItem>
        )}

        <div className="h-px bg-edge my-1.5 mx-1" />

        <MenuItem>
          {({ focus }) => {
            const hot = focus;
            return (
              <button type="button" onClick={() => logout()} className={cn(menuRow, hot && 'bg-surface-secondary text-content')}>
                <span className={menuIc}><Icons.signout /></span>
                <span>Sign out</span>
              </button>
            );
          }}
        </MenuItem>
      </MenuItems>
    </Menu>
  );
}

// ---------------------------------------------------------------------------
// TopNav
// ---------------------------------------------------------------------------

export default function TopNav({ onOpenBugReport }: { onOpenBugReport: () => void }) {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const verification = useLedgerVerification();

  const plan = user?.organisation?.plan;
  const primaryItems = PRIMARY_NAV.filter((i) => !i.requiresPlan || i.requiresPlan === plan);
  const orgName = user?.organisation?.name ?? '';

  return (
    <>
      {/* Desktop header: lg and up. The witnessed phase-1 + mobile-fix bar, unchanged.
          Below lg it yields entirely to MobileNav (ENGINE-BRIEF-MOBILE-NAV-v1.0 section 2). */}
      <header role="banner" className="hidden lg:block shrink-0 z-40">
        <div className="flex items-center gap-3.5 px-[26px] h-14 bg-surface border-b border-edge relative">
          <NavLink
            to="/"
            end
            aria-label="SpeyBooks home"
            className={cn('flex items-center gap-[9px] rounded-lg shrink-0', focusRing)}
          >
            <BrandMark />
            <BrandWord />
          </NavLink>

          <nav aria-label="Primary" className="hidden lg:flex items-center gap-[1px] shrink-0">
            {primaryItems.map((item) => (
              <NavLink key={item.to} to={item.to} end={item.end} className={({ isActive }) => primaryClass(isActive)}>
                {item.label}
              </NavLink>
            ))}
            <MoreMenu pathname={pathname} />
          </nav>

          <div className="flex-1 min-w-[8px]" />

          <div className="hidden lg:flex items-center gap-3.5">
            <OrgDisplay name={orgName} />
            <UserMenu onOpenBugReport={onOpenBugReport} />
          </div>
        </div>

        <VerifiedStrip state={verification} />
      </header>

      {/* Mobile chrome: below lg. The bottom-bar model replaces the retired hamburger
          drawer, not behind a flag (ENGINE-BRIEF-MOBILE-NAV-v1.0 section 2). */}
      <MobileNav onOpenBugReport={onOpenBugReport} />
    </>
  );
}
